import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import uniqid from 'uniqid';
import Case from 'case';
import {
  Box, Text, Button,
} from 'grommet';
import { useAppTheme } from '@Components/Context';
import { DropButton } from '@Components/Control';
import { buildDateLabel, shortenInt } from '@Helpers/util';
import { localAsset } from '@Helpers/asset';
import {
  StyledStatusBadge, StyledTooltip, StyledHoverText, StyledSeeDetailsButton,
} from './StyledListSearches';
import { StyledSVG, StyledHoverContainer } from '../StyledSharedComponents';


export const ResultsContainer = ({ small, children, ...rest }) => (
  <Box
    fill
    wrap={false}
    direction={small ? 'column' : 'row'}
    border={{
      color: 'var(--nav-border)', size: '1px', style: 'solid', side: 'bottom',
    }}
    align={small ? 'start' : 'center'}
    gap={small ? '0.75rem' : '0'}
    {...rest}
  >
    {children}
  </Box>
);

ResultsContainer.propTypes = {
  small: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

export const TruncatedText = ({
  small,
  rowData,
  uid,
  textColor,
  boxWidth,
  leftPad,
  verticalPad,
  rightPad,
  noDataText,
  highlightColor,
  searchPriorityIcon,
  showViewCount,
  viewCount,
}) => {
  const truncateText = (str) => ((str.length > 100) ? `${str.substring(0, 97)}...` : str);
  const displayText = rowData[uid] ? truncateText(rowData[uid]) : noDataText;
  const tooltipUid = uniqid();
  const priorityUid = uniqid();
  const isPrioritized = rowData.priority && rowData.priority === 'prioritized';

  return (
    <Box
      direction="row"
      gap="0.55rem"
      align="center"
      width={boxWidth}
      pad={{ left: isPrioritized ? '0' : leftPad, vertical: verticalPad, right: rightPad }}
      margin={{ left: isPrioritized ? '-1rem' : '0', right: isPrioritized ? '1rem' : '0' }}
    >
      {isPrioritized && (
        <Box pad={{ top: small ? '0.2rem' : '0.1rem' }}>
          <StyledSVG
            src={searchPriorityIcon}
            width={small ? '1rem' : '0.8rem'}
            height={small ? '1rem' : '0.8rem'}
            title="Prioritized"
            data-for={`search-priority-tooltip-${priorityUid}`}
            data-tip="Prioritized"
          />
          {!small && (
            <StyledTooltip
              id={`search-priority-tooltip-${priorityUid}`}
              arrowColor={highlightColor}
              backgroundColor={highlightColor}
              place="bottom"
              effect="solid"
              delayShow={300}
            />
          )}
        </Box>
      )}
      <Box direction="column" gap="0.25rem">
        <Text
          size={small ? '1.2rem' : '1rem'}
          color={textColor}
          weight={small ? 600 : 500}
          data-for={`${tooltipUid}-tooltip`}
          data-tip={rowData[uid]}
        >
          {displayText}
        </Text>
        {showViewCount && !!viewCount && (
          <Text size="0.8rem" color={textColor} weight={400}>
            {`(${shortenInt(viewCount)} views)`}
          </Text>
        )}
      </Box>
      {!small && rowData[uid] && rowData[uid].length > 100 && (
        <StyledTooltip
          id={`${tooltipUid}-tooltip`}
          arrowColor={highlightColor}
          backgroundColor={highlightColor}
          place="bottom"
          effect="solid"
          delayShow={300}
        />
      )}
    </Box>
  );
};

TruncatedText.propTypes = {
  small: PropTypes.bool.isRequired,
  showViewCount: PropTypes.bool.isRequired,
  viewCount: PropTypes.number,
  rowData: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    name: PropTypes.string,
    title: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    status: PropTypes.string.isRequired,
    priority: PropTypes.string,
    resultCount: PropTypes.number,
    unreviewedCount: PropTypes.number,
    viewCount: PropTypes.number,
    platforms: PropTypes.arrayOf(PropTypes.string.isRequired),
  }).isRequired,
  uid: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  boxWidth: PropTypes.string.isRequired,
  leftPad: PropTypes.string.isRequired,
  verticalPad: PropTypes.string.isRequired,
  rightPad: PropTypes.string,
  noDataText: PropTypes.string.isRequired,
  highlightColor: PropTypes.string.isRequired,
  searchPriorityIcon: PropTypes.string.isRequired,
};

const SearchControlButtons = ({
  small,
  buttonOnly = false,
  isMediumScreen,
  searchId,
  dashboardPath,
  seeDetailsIcon,
  selectStatusColor,
  data = null,
  leftPad = null,
  boxWidth = null,
  searchFeatures = null,
  submitSearchFeature = null,
}) => {
  const { theme: { page, button } } = useAppTheme();
  const showFeatureOptions = searchFeatures?.length > 0;

  const renderControlMenu = () => (
    <Box align="center" justify="center">
      <DropButton
        formIcons
        overridePosition={{ bottom: 'top' }}
        withCloseDropContent={(onClose) => (
          <Box
            round="var(--app-radius)"
            pad="1rem"
            direction="column"
            gap={small ? '1rem' : '0.65rem'}
            background={page.inputBg}
          >
            {searchFeatures.map((feature) => (
              <Button
                plain
                key={feature}
                onClick={() => {
                  submitSearchFeature(searchId, feature);
                  onClose();
                }}
              >
                <StyledHoverText weight={600} hoverColor={page.altHighlight}>
                  {`${Case.capital(feature)} search`}
                </StyledHoverText>
              </Button>
            ))}
          </Box>
        )}
      >
        {() => (
          <StyledHoverContainer hoverColor={button.primary} align="center" justify="center" pad={{ top: small ? '0.25rem' : '0.45rem' }}>
            <StyledSVG
              src={localAsset('images/more-options-icon.svg')}
              width={small ? '1.8rem' : '1.5rem'}
              height={small ? '1.2rem' : '1rem'}
              title="Search control options"
              data-for={`search-control-tooltip-${searchId}`}
              data-tip="Search controls"
              $fillColor={page.tableText}
              $hoverColor={page.highlight}
            />
            {!small && (
              <StyledTooltip
                id={`search-control-tooltip-${searchId}`}
                arrowColor={page.highlight}
                backgroundColor={page.highlight}
                place="bottom"
                effect="solid"
                delayShow={300}
              />
            )}
          </StyledHoverContainer>
        )}
      </DropButton>
    </Box>
  );

  if (buttonOnly) {
    return (
      <Box direction="row" gap="2.5rem">
        {showFeatureOptions && renderControlMenu()}
        <Button plain href={dashboardPath(searchId)}>
          <Box flex={false} width="1.6rem" height="1.6rem">
            <StyledSVG
              $fillColor={page.tableText}
              $hoverColor={page.highlight}
              src={seeDetailsIcon}
              width="1rem"
              height="1rem"
              title="Fully reviewed"
            />
          </Box>
        </Button>
      </Box>
    );
  }

  return (
    <Box width={boxWidth} direction="row" justify="start" align="center" pad={{ left: leftPad }}>
      <StyledStatusBadge background={selectStatusColor(data, 'background')}>
        <Text size="1rem" color={selectStatusColor(data, 'text')} weight={600}>
          {Case.capital(data)}
        </Text>
      </StyledStatusBadge>
      {!small && (
        <Box fill direction="row" justify="end" align="center" gap={isMediumScreen ? '1.5rem' : '3rem'} pad={{ right: '1rem' }}>
          {showFeatureOptions && renderControlMenu()}
          <StyledSeeDetailsButton
            plain
            textColor={page.tableText}
            hoverTextColor={page.highlight}
            href={dashboardPath(searchId)}
          >
            <Box wrap={false} direction="row" gap="0.5rem" align="center">
              {!isMediumScreen && (
                <Text size="1rem" className="seeDetailsText">
                  See details
                </Text>
              )}
              <Box flex={false} width="1rem" height="1rem">
                <StyledSVG
                  $fillColor={page.tableText}
                  $hoverColor={page.highlight}
                  className="seeDetailsIcon"
                  src={seeDetailsIcon}
                  width="1rem"
                  height="1rem"
                  title="Fully reviewed"
                />
              </Box>
            </Box>
          </StyledSeeDetailsButton>
        </Box>
      )}
    </Box>
  );
};

SearchControlButtons.propTypes = {
  small: PropTypes.bool.isRequired,
  buttonOnly: PropTypes.bool,
  isMediumScreen: PropTypes.bool.isRequired,
  dashboardPath: PropTypes.func.isRequired,
  searchId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  searchFeatures: PropTypes.arrayOf(PropTypes.string.isRequired),
  seeDetailsIcon: PropTypes.string.isRequired,
  selectStatusColor: PropTypes.func.isRequired,
  data: PropTypes.string,
  leftPad: PropTypes.string,
  boxWidth: PropTypes.string,
  submitSearchFeature: PropTypes.func,
};

export const SearchResultRow = ({
  small,
  isMediumScreen,
  showViewCount,
  showUnit,
  reviewEnabled,
  rowData,
  statusColorMap,
  seeDetailsIcon,
  searchPriorityIcon,
  primaryText,
  buttonHighlight,
  headerMap,
  dashboardPath,
  submitSearchFeature = null,
}) => {
  const selectStatusColor = (status, type) => {
    const currentStatus = statusColorMap[status];

    if (currentStatus) return currentStatus[type];

    return null;
  };

  const unreviewedCount = reviewEnabled ? rowData.unreviewedCount : null;

  const renderRowData = (uid) => {
    const currentHeader = _.find(headerMap, { uid });
    const isNumeric = ['resultCount', 'infringements', 'viewCount', 'unreviewedCount'].includes(uid);
    const noDataText = isNumeric ? '0' : '';
    const boxWidth = small ? 'auto' : currentHeader.size;
    let verticalPad = small ? '0' : '1.5rem';
    const leftPad = small ? '0' : '0.5rem';

    if (uid === 'name' || uid === 'title' || uid === 'unit') {
      verticalPad = small ? '0' : '0.65rem';
    }

    let resultCountDisplay = `${rowData[uid]} results`;

    if (uid === 'resultCount' && rowData[uid] === 1) {
      resultCountDisplay = `${rowData[uid]} result`;
    }

    if (uid === 'resultCount' && rowData[uid] >= 1 && reviewEnabled) {
      resultCountDisplay = `${rowData[uid] - unreviewedCount} of ${rowData[uid]} reviewed`;
    }

    switch (uid) {
      case 'date':
        return (
          <Box key={uniqid()} width={boxWidth} pad={{ left: leftPad, vertical: verticalPad }}>
            <Text size="1rem" color={primaryText}>
              {buildDateLabel(rowData.startDate, rowData.endDate, true)}
            </Text>
          </Box>
        );

      case 'status':
        return (
          <SearchControlButtons
            key={uniqid()}
            small={small}
            isMediumScreen={isMediumScreen}
            searchId={rowData.id}
            dashboardPath={dashboardPath}
            seeDetailsIcon={seeDetailsIcon}
            selectStatusColor={selectStatusColor}
            data={rowData[uid]}
            searchFeatures={rowData.features}
            leftPad={leftPad}
            boxWidth={boxWidth}
            submitSearchFeature={submitSearchFeature}
          />
        );

      case 'title':
      case 'name':
        return (
          <TruncatedText
            key={uniqid()}
            small={small}
            rowData={rowData}
            uid={uid}
            textColor={primaryText}
            boxWidth={small ? '75%' : currentHeader.size}
            leftPad={leftPad}
            verticalPad={verticalPad}
            rightPad={small ? '0' : '0.5rem'}
            noDataText={noDataText}
            highlightColor={buttonHighlight}
            searchPriorityIcon={searchPriorityIcon}
            showViewCount={showViewCount}
            viewCount={rowData.viewCount}
          />
        );

      case 'platforms':
        return (
          <Box key={uniqid()} width={boxWidth} pad={{ left: leftPad, vertical: verticalPad }}>
            <Text size="1rem" color={primaryText} weight={400}>
              {rowData[uid] ? rowData[uid].join(', ') : noDataText}
            </Text>
          </Box>
        );

      case 'resultCount':
        return (
          <Box
            key={uniqid()}
            width={boxWidth}
            pad={{ left: leftPad, vertical: verticalPad }}
            gap="0.35rem"
            direction="row"
            align="center"
          >
            <Text size="1rem" color={primaryText} weight={400}>
              {resultCountDisplay}
            </Text>
            {rowData[uid] >= 1 && reviewEnabled && unreviewedCount === 0 && (
              <Box flex={false} width="1rem" height="1rem">
                <StyledSVG
                  $fillColor="var(--app-success)"
                  src={localAsset('images/circled-checkmark-icon.svg')}
                  width={small ? '1rem' : '0.9rem'}
                  height={small ? '1rem' : '0.9rem'}
                  title="Fully reviewed"
                />
              </Box>
            )}
          </Box>
        );

      default:
        return (
          <Box key={uniqid()} width={boxWidth} pad={{ left: leftPad, vertical: verticalPad }}>
            <Text size="1rem" color={primaryText} weight={400}>
              {rowData[uid] || noDataText}
            </Text>
          </Box>
        );
    }
  };

  if (small) {
    return (
      <ResultsContainer small pad={{ vertical: '1rem' }}>
        <Box width="100%" direction="row" justify="between">
          {renderRowData('name')}
          {renderRowData('status')}
        </Box>
        {showUnit && (
          <Box width="100%" direction="row" gap="0.3rem">
            {renderRowData('unit')}
          </Box>
        )}
        <Box width="100%" direction="row" gap="0.3rem">
          {renderRowData('resultCount')}
        </Box>
        <Box width="100%" direction="row" justify="between">
          {renderRowData('date')}
          <SearchControlButtons
            small
            buttonOnly
            key={uniqid()}
            isMediumScreen={isMediumScreen}
            searchId={rowData.id}
            dashboardPath={dashboardPath}
            searchFeatures={rowData.features}
            seeDetailsIcon={seeDetailsIcon}
            selectStatusColor={selectStatusColor}
            submitSearchFeature={submitSearchFeature}
          />
        </Box>
      </ResultsContainer>
    );
  }

  return (
    <ResultsContainer small={false}>
      {headerMap.map(({ uid }) => renderRowData(uid))}
    </ResultsContainer>
  );
};

SearchResultRow.propTypes = {
  small: PropTypes.bool.isRequired,
  isMediumScreen: PropTypes.bool.isRequired,
  showViewCount: PropTypes.bool.isRequired,
  showUnit: PropTypes.bool.isRequired,
  reviewEnabled: PropTypes.bool.isRequired,
  rowData: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    name: PropTypes.string,
    title: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    status: PropTypes.string.isRequired,
    priority: PropTypes.string,
    resultCount: PropTypes.number,
    unreviewedCount: PropTypes.number,
    viewCount: PropTypes.number,
    unit: PropTypes.string,
    platforms: PropTypes.arrayOf(PropTypes.string.isRequired),
    features: PropTypes.arrayOf(PropTypes.string.isRequired),
  }).isRequired,
  statusColorMap: PropTypes.shape({
    running: PropTypes.shape({
      text: PropTypes.string.isRequired,
      background: PropTypes.string.isRequired,
    }).isRequired,
    completed: PropTypes.shape({
      text: PropTypes.string.isRequired,
      background: PropTypes.string.isRequired,
    }).isRequired,
    paused: PropTypes.shape({
      text: PropTypes.string.isRequired,
      background: PropTypes.string.isRequired,
    }).isRequired,
    scheduled: PropTypes.shape({
      text: PropTypes.string.isRequired,
      background: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  headerMap: PropTypes.arrayOf(PropTypes.shape({
    display: PropTypes.string.isRequired,
    uid: PropTypes.string.isRequired,
    size: PropTypes.string.isRequired,
  }).isRequired).isRequired,
  seeDetailsIcon: PropTypes.string.isRequired,
  searchPriorityIcon: PropTypes.string.isRequired,
  primaryText: PropTypes.string.isRequired,
  buttonHighlight: PropTypes.string.isRequired,
  dashboardPath: PropTypes.func.isRequired,
  submitSearchFeature: PropTypes.func,
};
